(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const initRoots = (root) => {
    if (!root || root.dataset.trybbReady === 'true') return;
    root.dataset.trybbReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const number = (key, fallback) => Number.parseFloat(root.dataset[key] || String(fallback));
    const integer = (key, fallback) => Number.parseInt(root.dataset[key] || String(fallback), 10);

    const threadColor = root.dataset.threadColor || '#B33A2F';
    const threadWidth = number('threadWidth', 3);
    const shadowWidth = number('threadShadowWidth', 6);
    const shadowOpacity = number('threadShadowOpacity', .4);
    const revealLead = clamp(number('threadRevealLead', 72) / 100, .45, .9);
    const organicVariation = clamp(number('threadOrganic', 8), 0, 24);
    const threadFollow = clamp(number('threadFollow', 18) / 100, .08, .35);
    const waypointGlow = clamp(number('waypointGlow', 55) / 100, 0, 1);
    const waypointPassedOpacity = clamp(number('waypointPassedOpacity', 62) / 100, .2, 1);

    const heroKickerDelay = Math.max(0, integer('heroKickerDelay', 900));
    const heroKickerDuration = Math.max(100, integer('heroKickerDuration', 900));
    const heroHeadingDelay = Math.max(0, integer('heroHeadingDelay', 2500));
    const heroHeadingDuration = Math.max(100, integer('heroHeadingDuration', 1100));
    const heroSubtitleDelay = Math.max(0, integer('heroSubtitleDelay', 4500));
    const heroSubtitleDuration = Math.max(100, integer('heroSubtitleDuration', 1400));
    const overlayStart = clamp(number('heroOverlayStart', .85), 0, 1);
    const overlayEnd = clamp(number('heroOverlayEnd', .35), 0, 1);
    const overlayDuration = Math.max(100, integer('heroOverlayDuration', 3200));
    const heroParallax = clamp(number('heroParallax', 12) / 100, 0, .3);
    const quietParallax = clamp(number('quietParallax', 2) / 100, 0, .08);
    const quietGrain = clamp(number('quietGrain', .06), 0, .2);
    const editorialDim = clamp(number('editorialDim', .88), .7, 1);

    const lessonsMarkDelay = Math.max(0, integer('lessonsMarkDelay', 0));
    const lessonsLeftDelay = Math.max(0, integer('lessonsLeftDelay', 250));
    const lessonsRightDelay = Math.max(0, integer('lessonsRightDelay', 600));
    const lessonsStagger = Math.max(0, integer('lessonsStagger', 750));
    const finaleHeadingDelay = Math.max(0, integer('finaleHeadingDelay', 0));
    const finaleSubtitleDelay = Math.max(0, integer('finaleSubtitleDelay', 450));
    const finaleCtaDelay = Math.max(0, integer('finaleCtaDelay', 850));

    root.style.setProperty('--tr-red', threadColor);
    root.style.setProperty('--tr-thread-width', `${threadWidth}px`);
    root.style.setProperty('--tr-thread-shadow-width', `${shadowWidth}px`);
    root.style.setProperty('--tr-thread-shadow-opacity', `${shadowOpacity}`);
    root.style.setProperty('--tr-waypoint-glow', `${waypointGlow}`);
    root.style.setProperty('--tr-waypoint-passed-opacity', `${waypointPassedOpacity}`);
    root.style.setProperty('--tr-hero-overlay-start', `${overlayStart}`);
    root.style.setProperty('--tr-hero-overlay-end', `${overlayEnd}`);
    root.style.setProperty('--tr-hero-overlay-duration', `${overlayDuration}ms`);
    root.style.setProperty('--tr-hero-kicker-duration', `${heroKickerDuration}ms`);
    root.style.setProperty('--tr-hero-heading-duration', `${heroHeadingDuration}ms`);
    root.style.setProperty('--tr-hero-subtitle-duration', `${heroSubtitleDuration}ms`);
    root.style.setProperty('--tr-quiet-grain-opacity', `${quietGrain}`);
    root.style.setProperty('--tr-editorial-dim', `${editorialDim}`);
    root.style.setProperty('--tr-lessons-mark-delay', `${lessonsMarkDelay}ms`);
    root.style.setProperty('--tr-lessons-left-delay', `${lessonsLeftDelay}ms`);
    root.style.setProperty('--tr-lessons-right-delay', `${lessonsRightDelay}ms`);
    root.style.setProperty('--tr-lessons-stagger', `${lessonsStagger}ms`);
    root.style.setProperty('--tr-finale-heading-delay', `${finaleHeadingDelay}ms`);
    root.style.setProperty('--tr-finale-subtitle-delay', `${finaleSubtitleDelay}ms`);
    root.style.setProperty('--tr-finale-cta-delay', `${finaleCtaDelay}ms`);

    const hero = root.querySelector('.trybb-roots__hero');
    const heroMedia = root.querySelector('.trybb-roots__hero-media');
    const quietMedia = root.querySelector('[data-trybb-quiet-media]');
    const heroKicker = root.querySelector('[data-trybb-hero-reveal="kicker"]');
    const heroHeadingItems = [...root.querySelectorAll('[data-trybb-hero-reveal="heading"]')];
    const heroSubtitle = root.querySelector('[data-trybb-hero-reveal="subtitle"]');

    requestAnimationFrame(() => root.classList.add('is-hero-awake'));
    if (reducedMotion) {
      heroKicker?.classList.add('is-visible');
      heroHeadingItems.forEach((item) => item.classList.add('is-visible'));
      heroSubtitle?.classList.add('is-visible');
    } else {
      window.setTimeout(() => heroKicker?.classList.add('is-visible'), heroKickerDelay);
      window.setTimeout(() => heroHeadingItems.forEach((item) => item.classList.add('is-visible')), heroHeadingDelay);
      window.setTimeout(() => heroSubtitle?.classList.add('is-visible'), heroSubtitleDelay);
    }

    const revealItems = [...root.querySelectorAll('[data-trybb-reveal]')];
    if ('IntersectionObserver' in window && !reducedMotion) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.14 });
      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    const lessonItems = [...root.querySelectorAll('[data-trybb-lesson]')];
    if ('IntersectionObserver' in window && !reducedMotion) {
      const lessonObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            lessonObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -13% 0px', threshold: 0.22 });
      lessonItems.forEach((item) => lessonObserver.observe(item));
    } else {
      lessonItems.forEach((item) => item.classList.add('is-visible'));
    }

    const scenes = [...root.querySelectorAll('[data-trybb-scene]')];
    const setActiveScene = () => {
      const focusY = window.innerHeight * .47;
      let activeIndex = 0;
      let bestDistance = Infinity;
      scenes.forEach((scene, index) => {
        const rect = scene.getBoundingClientRect();
        const center = rect.top + rect.height * .45;
        const distance = Math.abs(center - focusY);
        if (distance < bestDistance) {
          bestDistance = distance;
          activeIndex = index;
        }
      });
      scenes.forEach((scene, index) => {
        scene.classList.toggle('is-active-scene', index === activeIndex);
        scene.classList.toggle('is-past-scene', index < activeIndex);
      });
    };

    const svg = root.querySelector('[data-trybb-thread-svg]');
    const lineGroup = root.querySelector('[data-trybb-thread-lines]');
    const shadowGroup = root.querySelector('[data-trybb-thread-shadows]');
    const waypoints = [...root.querySelectorAll('[data-trybb-waypoint]')];

    let segments = [];
    let totalLength = 0;
    let buildFrame = 0;
    let scrollFrame = 0;
    let threadFrame = 0;
    let targetReveal = 0;
    let currentReveal = 0;

    const waypointGeometry = (element, index) => {
      const rootRect = root.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const deterministicWave = index === 0 || index === waypoints.length - 1
        ? 0
        : Math.sin(index * 1.71) * organicVariation;
      return {
        element,
        x: elementRect.left - rootRect.left + elementRect.width / 2 + deterministicWave,
        y: elementRect.top - rootRect.top + elementRect.height / 2,
        radius: Math.max(elementRect.width, elementRect.height) / 2 + Math.max(threadWidth, shadowWidth) / 2 + 1
      };
    };

    const normalize = (x, y) => {
      const magnitude = Math.hypot(x, y) || 1;
      return { x: x / magnitude, y: y / magnitude };
    };

    /*
      Every connection is its own SVG path. This is essential: dash animation
      restarts on separate subpaths inside one compound path, which caused all
      later sections to appear fully drawn. Individual paths let us reveal and
      erase the thread continuously while preserving a true gap at waypoints.
    */
    const threadSegmentsThrough = (points) => {
      if (points.length < 2) return [];
      const output = [];

      for (let index = 0; index < points.length - 1; index += 1) {
        const previous = points[index - 1] || points[index];
        const current = points[index];
        const next = points[index + 1];
        const after = points[index + 2] || next;

        const segmentDirection = normalize(next.x - current.x, next.y - current.y);
        const incomingTangent = normalize(next.x - previous.x, next.y - previous.y);
        const outgoingTangent = normalize(after.x - current.x, after.y - current.y);

        const start = {
          x: current.x + segmentDirection.x * current.radius,
          y: current.y + segmentDirection.y * current.radius
        };
        const end = {
          x: next.x - segmentDirection.x * next.radius,
          y: next.y - segmentDirection.y * next.radius
        };

        const distance = Math.hypot(end.x - start.x, end.y - start.y);
        const handle = Math.max(28, distance * (.27 + Math.sin((index + 1) * 2.13) * .018));
        const c1 = {
          x: start.x + incomingTangent.x * handle,
          y: start.y + incomingTangent.y * handle
        };
        const c2 = {
          x: end.x - outgoingTangent.x * handle,
          y: end.y - outgoingTangent.y * handle
        };

        output.push({
          startY: start.y,
          endY: end.y,
          d: `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${end.x.toFixed(2)} ${end.y.toFixed(2)}`
        });
      }

      return output;
    };

    const applyThreadReveal = (revealedLength) => {
      segments.forEach((segment) => {
        const localReveal = clamp(revealedLength - segment.cumulativeStart, 0, segment.length);
        const offset = segment.length - localReveal;
        segment.revealPaths.forEach((path) => {
          path.style.strokeDashoffset = `${offset}`;
        });
      });
    };

    const cumulativeLengthAtY = (targetY) => {
      if (!segments.length) return 0;
      let revealed = 0;

      for (const segment of segments) {
        const minY = Math.min(segment.startY, segment.endY);
        const maxY = Math.max(segment.startY, segment.endY);

        if (targetY >= maxY) {
          revealed += segment.length;
          continue;
        }
        if (targetY <= minY) break;

        let low = 0;
        let high = segment.length;
        for (let iteration = 0; iteration < 22; iteration += 1) {
          const middle = (low + high) / 2;
          const point = segment.line.getPointAtLength(middle);
          if (point.y <= targetY) low = middle;
          else high = middle;
        }
        revealed += low;
        break;
      }

      return clamp(revealed, 0, totalLength);
    };

    const animateThread = () => {
      threadFrame = 0;
      const difference = targetReveal - currentReveal;
      currentReveal += difference * threadFollow;
      if (Math.abs(difference) < .18) currentReveal = targetReveal;
      applyThreadReveal(currentReveal);
      if (Math.abs(targetReveal - currentReveal) >= .18) threadFrame = requestAnimationFrame(animateThread);
    };

    const updateScrollEffects = () => {
      scrollFrame = 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (hero && heroMedia && !reducedMotion) {
        const rect = hero.getBoundingClientRect();
        const progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);
        root.style.setProperty('--tr-hero-parallax-y', `${(progress * viewportHeight * heroParallax).toFixed(2)}px`);
      }

      if (quietMedia && !reducedMotion) {
        const rect = quietMedia.getBoundingClientRect();
        const centerDelta = (rect.top + rect.height / 2) - viewportHeight / 2;
        const shift = clamp(-centerDelta / viewportHeight, -1, 1) * viewportHeight * quietParallax;
        root.style.setProperty('--tr-quiet-parallax-y', `${shift.toFixed(2)}px`);
      }

      setActiveScene();
      if (!segments.length) return;
      const rect = root.getBoundingClientRect();
      const revealY = clamp(-rect.top + viewportHeight * revealLead, 0, root.scrollHeight);

      waypoints.forEach((waypoint) => {
        const waypointRect = waypoint.getBoundingClientRect();
        const centerY = waypointRect.top - rect.top + waypointRect.height / 2;
        const arrivalWindow = Math.max(72, waypointRect.height * 1.45);
        const delta = revealY - centerY;
        waypoint.classList.toggle('is-thread-arriving', Math.abs(delta) <= arrivalWindow);
        waypoint.classList.toggle('is-thread-passed', delta > arrivalWindow);
      });

      targetReveal = cumulativeLengthAtY(revealY);
      if (reducedMotion) {
        currentReveal = targetReveal;
        applyThreadReveal(currentReveal);
      } else if (!threadFrame) {
        threadFrame = requestAnimationFrame(animateThread);
      }
    };

    const createPath = (className, d) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', className);
      path.setAttribute('d', d);
      return path;
    };

    const buildPath = () => {
      buildFrame = 0;
      if (!svg || !lineGroup || !shadowGroup || waypoints.length < 2) return;
      const width = root.clientWidth;
      const height = root.scrollHeight;
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);

      lineGroup.replaceChildren();
      shadowGroup.replaceChildren();
      svg.querySelector('defs[data-trybb-thread-defs]')?.remove();
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.setAttribute('data-trybb-thread-defs', '');
      svg.insertBefore(defs, svg.firstChild);
      segments = [];
      totalLength = 0;

      const definitions = threadSegmentsThrough(waypoints.map(waypointGeometry));
      definitions.forEach((definition, index) => {
        const isRoadToUnknown = index === definitions.length - 1;
        const shadowPath = createPath(`trybb-roots__thread-shadow${isRoadToUnknown ? ' is-road-to-unknown' : ''}`, definition.d);
        const linePath = createPath(`trybb-roots__thread-line${isRoadToUnknown ? ' is-road-to-unknown' : ''}`, definition.d);
        shadowGroup.appendChild(shadowPath);
        lineGroup.appendChild(linePath);

        const segmentLength = linePath.getTotalLength();
        let revealPaths = [linePath, shadowPath];

        if (isRoadToUnknown) {
          const maskId = `trybb-road-unknown-${root.id || 'roots'}-${index}`.replace(/[^a-zA-Z0-9_-]/g, '-');
          const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
          mask.setAttribute('id', maskId);
          mask.setAttribute('maskUnits', 'userSpaceOnUse');
          mask.setAttribute('x', '0');
          mask.setAttribute('y', '0');
          mask.setAttribute('width', String(width));
          mask.setAttribute('height', String(height));

          const revealPath = createPath('trybb-roots__thread-mask-reveal', definition.d);
          revealPath.setAttribute('fill', 'none');
          revealPath.setAttribute('stroke', '#fff');
          revealPath.setAttribute('stroke-width', String(Math.max(shadowWidth + 6, threadWidth + 8)));
          revealPath.setAttribute('stroke-linecap', 'round');
          revealPath.style.strokeDasharray = `${segmentLength}`;
          revealPath.style.strokeDashoffset = `${segmentLength}`;
          mask.appendChild(revealPath);
          defs.appendChild(mask);

          linePath.setAttribute('mask', `url(#${maskId})`);
          shadowPath.setAttribute('mask', `url(#${maskId})`);
          revealPaths = [revealPath];
        } else {
          revealPaths.forEach((path) => {
            path.style.strokeDasharray = `${segmentLength}`;
            path.style.strokeDashoffset = `${segmentLength}`;
          });
        }

        segments.push({
          ...definition,
          line: linePath,
          shadow: shadowPath,
          revealPaths,
          length: segmentLength,
          cumulativeStart: totalLength
        });
        totalLength += segmentLength;
      });

      currentReveal = clamp(currentReveal, 0, totalLength);
      targetReveal = clamp(targetReveal, 0, totalLength);
      applyThreadReveal(currentReveal);
      updateScrollEffects();
    };

    const scheduleBuild = () => {
      if (buildFrame) cancelAnimationFrame(buildFrame);
      buildFrame = requestAnimationFrame(buildPath);
    };
    const scheduleScroll = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(updateScrollEffects);
    };

    window.addEventListener('resize', scheduleBuild, { passive: true });
    window.addEventListener('scroll', scheduleScroll, { passive: true });
    window.addEventListener('load', scheduleBuild, { once: true });
    if ('ResizeObserver' in window) new ResizeObserver(scheduleBuild).observe(root);
    scheduleBuild();
    setActiveScene();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-trybb-roots]').forEach(initRoots);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
